/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import SEO from '../components/SEO';
import {
  Grid, Typography, Container, Box, Button, IconButton
} from '@mui/material';
import PageBanner from '../components/PageBanner';
import SectionSidebar from '../components/SectionSidebar';
import { poojas as allPoojas, Pooja } from '../data/pooja-data';
import { Link as RouterLink } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';

const GOLD = '#d4af37';
const GOLD_LIGHT = '#e5c158';

const DEITY_MAP: Record<string, { gradient: string; label: string; icon: string }> = {
  BHAGAVATHY: { gradient: 'linear-gradient(135deg, #c0392b, #e74c3c)', label: 'Devi', icon: '\u0D26\u0D47' },
  SIVA:       { gradient: 'linear-gradient(135deg, #2c3e50, #34495e)', label: 'Shiva', icon: '\u0D36' },
  VISHNU:     { gradient: 'linear-gradient(135deg, #1a5276, #2980b9)', label: 'Vishnu', icon: '\u0D35' },
  GANAPATHY:  { gradient: 'linear-gradient(135deg, #d35400, #e67e22)', label: 'Ganapathy', icon: '\u0D17' },
  AYYAPPA:    { gradient: 'linear-gradient(135deg, #117a65, #1abc9c)', label: 'Ayyappa', icon: '\u0D05' },
  GENERAL:    { gradient: 'linear-gradient(135deg, #7d6608, #b8960c)', label: 'General', icon: '\u0D13\u0D02' },
};

const FEATURES = [
  { icon: TempleHinduIcon, title: 'Sacred Rituals', desc: 'Performed by experienced temple priests following traditional Vedic practices' },
  { icon: CalendarMonthIcon, title: 'Flexible Dates', desc: 'Choose your preferred date and time for the pooja ceremony' },
  { icon: SelfImprovementIcon, title: 'Divine Blessings', desc: 'Each pooja is performed for well-being, prosperity, health, and spiritual growth' },
];

const FAQS = [
  { q: 'How do I book a pooja?', a: "Click 'Open Booking Desk' and follow the booking flow to select poojas, provide devotee details and preferred date." },
  { q: 'Can I choose a specific priest?', a: 'Priest requests can be added in the booking notes; confirmation depends on availability.' },
  { q: 'Is payment required at booking?', a: 'Payment is collected securely during checkout; you may complete booking with provisional slots when allowed by the office.' },
];

export default function Poojas() {
  const [expandedFaq, setExpandedFaq] = React.useState<number | null>(null);
  const visible: Pooja[] = allPoojas.slice();

  // Group poojas by deity for the preview
  const deityGroups = Object.entries(
    visible.reduce<Record<string, Pooja[]>>((acc, p) => {
      const key = p.deity || 'GENERAL';
      if (!acc[key]) acc[key] = [];
      acc[key].push(p);
      return acc;
    }, {})
  );

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO
        title="Poojas & Vazhipads | Kottayil Temple Vazhipadu"
        description="Explore and book sacred poojas and vazhipads at Kottayil Temple (Muchukunnu Sri Kotta-Kovilakam Kshethram). Daily rituals, special pooja details, festival ceremonies, vazhipadu list and online booking."
        path="/poojas"
        keywords="kottayil temple vazhipadu, kottayil temple Muchukunnu pooja, pooja details, special pooja, kottayil temple pooja booking, kottayil shiva temple pooja, vazhipadu booking online, temple poojas, vazhipads, online pooja booking Kerala, Hindu rituals, Muchukunnu temple poojas, daily pooja, festival pooja, nithya pooja, Kotta Kovilakam pooja, Kozhikode temple pooja, കൊട്ടയിൽ ക്ഷേത്രം വഴിപാട്, മുച്ചുകുന്ന് ക്ഷേത്രം പൂജ"
      />
      <PageBanner title="Poojas & Vazhipads" />

      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>

          {/* ── Main Content ── */}
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ HERO INTRO SECTION ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '28px',
              border: '1px solid rgba(0,0,0,0.04)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
            }}>
              {/* Dark header */}
              <Box sx={{
                background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2520 50%, #1a1a1a 100%)',
                px: { xs: 2.5, sm: 3.5 }, pt: 3.5, pb: 5,
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{
                  position: 'absolute', inset: 0, opacity: 0.04,
                  backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                  backgroundSize: '18px 18px',
                }} />
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                  background: 'linear-gradient(90deg, transparent, ' + GOLD + ', transparent)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 3s infinite linear',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '-200% 0' },
                    '100%': { backgroundPosition: '200% 0' },
                  },
                }} />
                <Box sx={{
                  position: 'absolute', top: -30, right: -30,
                  width: 120, height: 120, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)',
                }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 2 }}>
                  <Box sx={{
                    width: 52, height: 52, borderRadius: '16px',
                    background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(212,175,55,0.3)',
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 26, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontWeight: 900, fontSize: { xs: '1.3rem', sm: '1.5rem' }, color: '#fff',
                      lineHeight: 1.2, mb: 0.3,
                    }}>
                      Sacred Offerings
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                      Discover poojas, homams, and vazhipad offerings
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{
                  position: 'absolute', bottom: -1, left: 0, right: 0, height: 28,
                  bgcolor: '#fff',
                  borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
                }} />
              </Box>

              {/* Body */}
              <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 0.5, pb: 3 }}>
                <Typography sx={{
                  fontSize: '0.88rem', color: '#666', lineHeight: 1.8, mb: 2.5,
                }}>
                  A pooja is a ritual offering performed to honour the deity and seek blessings.
                  Offerings vary from simple archana and abhishekam to elaborate homams and seva packages.
                  Each pooja has a recommended time, suggested materials, and a customary fee that supports temple upkeep.
                </Typography>

                {/* Feature pills */}
                <Grid container spacing={2}>
                  {FEATURES.map((feat, idx) => (
                    <Grid item xs={12} sm={4} key={idx}>
                      <Box sx={{
                        px: 2, py: 2,
                        borderRadius: '18px',
                        bgcolor: 'rgba(212,175,55,0.03)',
                        border: '1px solid rgba(212,175,55,0.06)',
                        height: '100%',
                        transition: 'all 300ms ease',
                        '&:hover': {
                          bgcolor: 'rgba(212,175,55,0.06)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(212,175,55,0.08)',
                        },
                      }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: '12px',
                          background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          mb: 1.5,
                          boxShadow: '0 4px 12px rgba(212,175,55,0.2)',
                        }}>
                          <feat.icon sx={{ fontSize: 18, color: '#fff' }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a1a', mb: 0.5 }}>
                          {feat.title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#bbb', lineHeight: 1.5 }}>
                          {feat.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* ══════ DEITY OFFERINGS PREVIEW ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '28px',
              border: '1px solid rgba(0,0,0,0.04)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
            }}>
              <Box sx={{
                px: { xs: 2.5, sm: 3.5 }, py: 2.5,
                borderBottom: '1px solid rgba(0,0,0,0.04)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LocalFireDepartmentIcon sx={{ fontSize: 20, color: GOLD }} />
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#1a1a1a' }}>
                    Available Offerings
                  </Typography>
                  <Box sx={{
                    px: 1.2, py: 0.3, borderRadius: '10px',
                    bgcolor: 'rgba(212,175,55,0.08)',
                  }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: GOLD }}>
                      {visible.length} poojas
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Deity groups */}
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                <Grid container spacing={2}>
                  {deityGroups.map(([deityKey, poojas]) => {
                    const deity = DEITY_MAP[deityKey] || DEITY_MAP.GENERAL;
                    return (
                      <Grid item xs={6} sm={4} md={4} key={deityKey}>
                        <Box sx={{
                          borderRadius: '20px',
                          overflow: 'hidden',
                          border: '1px solid rgba(0,0,0,0.04)',
                          transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
                            '& .deityHeader': { pb: 3.5 },
                            '& .deityOrb': { transform: 'scale(1.15) rotate(8deg)' },
                          },
                        }}>
                          {/* Deity color header */}
                          <Box className="deityHeader" sx={{
                            background: deity.gradient,
                            px: 2, pt: 2, pb: 3,
                            position: 'relative',
                            transition: 'padding 350ms ease',
                          }}>
                            <Box sx={{
                              position: 'absolute', inset: 0, opacity: 0.08,
                              backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                              backgroundSize: '10px 10px',
                            }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                              <Box className="deityOrb" sx={{
                                width: 40, height: 40, borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(8px)',
                                border: '1.5px solid rgba(255,255,255,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                              }}>
                                <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', fontWeight: 700 }}>
                                  {deity.icon}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>
                                  {deity.label}
                                </Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.55)' }}>
                                  {poojas.length} offering{poojas.length > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{
                              position: 'absolute', bottom: -1, left: 0, right: 0, height: 16,
                              bgcolor: '#fff',
                              borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
                            }} />
                          </Box>

                          {/* Pooja list */}
                          <Box sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                            {poojas.slice(0, 3).map((p, i) => (
                              <Box key={p.id} sx={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                py: 0.8, px: 1,
                                borderBottom: i < Math.min(poojas.length, 3) - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
                              }}>
                                <Typography sx={{
                                  fontSize: '0.7rem', fontWeight: 600, color: '#555',
                                  flex: 1, mr: 1,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                  {p.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: GOLD, flexShrink: 0 }}>
                                  {'\u20B9'}{p.price.toLocaleString()}
                                </Typography>
                              </Box>
                            ))}
                            {poojas.length > 3 && (
                              <Typography sx={{
                                fontSize: '0.62rem', color: '#ccc', textAlign: 'center', mt: 0.8,
                              }}>
                                +{poojas.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

            {/* ══════ BOOKING DESK CTA ══════ */}
            <Box sx={{
              borderRadius: '28px',
              overflow: 'hidden',
              position: 'relative',
              background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2520 50%, #1a1a1a 100%)',
              mb: 3.5,
              transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.12)',
                '& .ctaArrow': { transform: 'translateX(4px)' },
              },
            }}>
              <Box sx={{
                position: 'absolute', inset: 0, opacity: 0.04,
                backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                backgroundSize: '18px 18px',
              }} />
              <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: 'linear-gradient(90deg, transparent, ' + GOLD + ', transparent)',
              }} />
              <Box sx={{
                position: 'absolute', bottom: -30, right: -30,
                width: 160, height: 160, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%)',
              }} />

              <Box sx={{
                px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 },
                display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 3, position: 'relative', zIndex: 2,
              }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '20px',
                  background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 10px 32px rgba(212,175,55,0.35)',
                  flexShrink: 0,
                }}>
                  <BookOnlineIcon sx={{ fontSize: 30, color: '#fff' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{
                    fontWeight: 900, fontSize: { xs: '1.2rem', sm: '1.4rem' },
                    color: '#fff', mb: 0.5, lineHeight: 1.2,
                  }}>
                    Ready to Book?
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6,
                    maxWidth: 400,
                  }}>
                    Open the booking desk to select your poojas, provide devotee details, choose a date, and complete your offering.
                  </Typography>
                </Box>
                <Button
                  component={RouterLink}
                  to="/poojas/booking"
                  endIcon={<ArrowForwardIcon className="ctaArrow" sx={{ fontSize: '18px !important', transition: 'transform 300ms ease' }} />}
                  sx={{
                    background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
                    color: '#fff', fontWeight: 800,
                    px: 4, py: 1.5, borderRadius: '16px',
                    fontSize: '0.9rem', textTransform: 'none',
                    boxShadow: '0 8px 28px rgba(212,175,55,0.35)',
                    whiteSpace: 'nowrap', flexShrink: 0,
                    transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '&:hover': {
                      transform: 'scale(1.03)',
                      boxShadow: '0 12px 36px rgba(212,175,55,0.45)',
                    },
                  }}
                >
                  Open Booking Desk
                </Button>
              </Box>
            </Box>

            {/* ══════ FAQ SECTION ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '28px',
              border: '1px solid rgba(0,0,0,0.04)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}>
              <Box sx={{
                px: { xs: 2.5, sm: 3.5 }, py: 2.5,
                borderBottom: '1px solid rgba(0,0,0,0.04)',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <StarOutlineIcon sx={{ fontSize: 20, color: GOLD }} />
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#1a1a1a' }}>
                  Frequently Asked Questions
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2 }}>
                {FAQS.map((faq, idx) => (
                  <Box key={idx} sx={{ mb: idx < FAQS.length - 1 ? 1 : 0 }}>
                    <Box
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1.5,
                        borderRadius: '14px',
                        cursor: 'pointer',
                        transition: 'all 250ms ease',
                        bgcolor: expandedFaq === idx ? 'rgba(212,175,55,0.04)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(212,175,55,0.04)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '10px',
                          bgcolor: expandedFaq === idx ? GOLD : 'rgba(212,175,55,0.08)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 250ms ease',
                        }}>
                          <Typography sx={{
                            fontSize: '0.7rem', fontWeight: 800,
                            color: expandedFaq === idx ? '#fff' : GOLD,
                          }}>
                            {idx + 1}
                          </Typography>
                        </Box>
                        <Typography sx={{
                          fontWeight: 700, fontSize: '0.85rem',
                          color: expandedFaq === idx ? '#1a1a1a' : '#555',
                          transition: 'color 250ms ease',
                        }}>
                          {faq.q}
                        </Typography>
                      </Box>
                      <ExpandMoreIcon sx={{
                        fontSize: 20, color: '#ccc',
                        transform: expandedFaq === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 300ms ease',
                      }} />
                    </Box>
                    {expandedFaq === idx && (
                      <Box sx={{
                        px: 2, pb: 1.5, pt: 0.5, ml: 5.5,
                        animation: 'fadeSlide 300ms ease',
                        '@keyframes fadeSlide': {
                          '0%': { opacity: 0, transform: 'translateY(-8px)' },
                          '100%': { opacity: 1, transform: 'translateY(0)' },
                        },
                      }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#999', lineHeight: 1.7 }}>
                          {faq.a}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

          </Grid>

          {/* ── Sidebar ── */}
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="poojas" activePath="/poojas" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
